import { useEffect } from 'react'
import dayjs from 'dayjs'
import { Form, Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import * as Yup from 'yup'

import { countryCodeList, getCountryLabel, getGenderLabel } from 'config'
import { logEvent } from 'lib/analytics'
import cookie from 'lib/cookie'
import { ROUTE_PATH } from 'lib/paths'
import { getRedirectUrl } from 'lib/router'
import { getDeviceId } from 'lib/user'
import { Gender, SNSInfo } from 'lib/_types'
import { addNotification } from 'store/slices/notifications'
import { useBanWord, useResponsive, useUploadImage } from 'hooks'
import { useSignUpMutation } from 'store/api/yay/user'
import { Link, useNavigate } from 'features/i18n/router'

import { FormikInput } from 'components'
import Button from 'components/Button'
import EditImage from 'components/EditImage'

import './style.scss'

const SignUpFormModal = ({ location, snsInfo }) => {
  const { isContainBanWord, showDetectedBanWordAlert } = useBanWord()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isMobile } = useResponsive()
  const [signUp] = useSignUpMutation()
  const { upload } = useUploadImage()

  const _snsInfo: SNSInfo = JSON.parse(snsInfo ?? '{}')

  useEffect(() => {
    if (!_snsInfo.type || !_snsInfo.uid) navigate(location.pathname)
  }, [_snsInfo])

  const handleSubmit = async (values: any) => {
    if (isContainBanWord(values.nickname, 'user')) {
      showDetectedBanWordAlert()
    } else {
      const data: any = {
        nickname: values.nickname,
        birth_date: values.birth_date,
        gender: values.gender,
        country_code: values.country_code,
        referral_code: values.referral_code,
        uuid: getDeviceId(),
        sns_info: {
          type: _snsInfo.type,
          uid: _snsInfo.uid,
        },
      }

      const _uploads = []

      if (values.profile_icon instanceof File) {
        _uploads.push(
          upload([values.profile_icon], 'user_avatar').then((a) => {
            if (a[0]) {
              data.profile_icon_filename = a[0].fileName
            }
          })
        )
      }

      try {
        await Promise.all(_uploads)
      } catch {}

      const signUpResponse = await signUp(data)

      if (
        'data' in signUpResponse &&
        signUpResponse?.data?.result === 'success'
      ) {
        logEvent.account_sign_up({
          type: _snsInfo?.type,
        })

        cookie.userId.set(
          signUpResponse?.data?.user_id || signUpResponse?.data?.id
        )

        // // Traditional route change to reload application, allowing an app to be aware of a new cookies.
        window.location.href = window.decodeURIComponent(
          getRedirectUrl(window.location.href) ?? '/'
        )
      } else if ('error' in signUpResponse) {
        const error = signUpResponse?.error as {
          status: number
          data: {
            result: string
            message: string
            error_code: number
          }
        }

        dispatch(
          addNotification({
            message: `${error?.data?.message} (${error?.data?.error_code})`,
            type: 'error',
          })
        )
      }
    }
  }

  return (
    <div className="Modal__wrapper Modal__wrapper--auto-h SignUpFormModal">
      {isMobile && (
        <div className="Modal__header">
          <a
            className="Modal__header__close"
            onClick={() => navigate(location.pathname)}
          ></a>

          <Link to={ROUTE_PATH.HOME}>
            <img
              className="Header__yay_logo"
              src="/images/logotype.svg"
              alt="yay! logo"
            />
          </Link>
        </div>
      )}

      <div className="SignUpFormModal__panel">
        <div className="SignUpFormModal__panel__wrapper">
          <h3 className="SignUpFormModal__panel__h3">
            {t('sign_up_form.title')}
          </h3>

          <Formik
            validationSchema={Yup.object().shape({
              nickname: Yup.string().required(),
              birth_date: Yup.date().required(),
              gender: Yup.string().required(),
              country_code: Yup.string().required(),
            })}
            isInitialValid={false}
            initialValues={{
              profile_icon: _snsInfo.profile_image ?? '',
              nickname: _snsInfo.nickname ?? '',
              birth_date: '',
              gender: _snsInfo.gender ?? Gender.UNSET,
              country_code: 'JP',
            }}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, isSubmitting, isValid }) => (
              <Form>
                <EditImage
                  id="avatar"
                  url={values.profile_icon}
                  defaultURL="/images/ProfileImage/Default-Profile-Icons-Sprint35.png"
                  reUpload
                  alt="avatar"
                  className="SignUpFormModal__form__avatar"
                  onSelect={(file) => {
                    setFieldValue('profile_icon', file)
                  }}
                />

                <div className="SignUpFormModal__form__field">
                  <h4 className="SignUpFormModal__form__field__label">
                    {t('common.nickname')}
                  </h4>

                  <FormikInput hideError name="nickname" maxLength={20} />
                </div>

                <div className="SignUpFormModal__form__field">
                  <h4 className="SignUpFormModal__form__field__label">
                    {t('common.birthdate')}
                  </h4>

                  <FormikInput
                    hideError
                    type="date"
                    name="birth_date"
                    min="1950-01-01"
                    max={dayjs().format('YYYY-MM-DD')}
                  />
                </div>

                <div className="SignUpFormModal__form__field">
                  <h4 className="SignUpFormModal__form__field__label">
                    {t('common.gender')}
                  </h4>

                  <FormikInput hideError as="select" name="gender">
                    {[-1, 0, 1].map((gender) => (
                      <option key={gender} value={gender}>
                        {getGenderLabel(gender)}
                      </option>
                    ))}
                  </FormikInput>
                </div>

                <div className="SignUpFormModal__form__field">
                  <h4 className="SignUpFormModal__form__field__label">
                    {t('common.country')}
                  </h4>

                  <FormikInput hideError as="select" name="country_code">
                    {countryCodeList.map((countryCode) => (
                      <option key={countryCode} value={countryCode}>
                        {getCountryLabel(countryCode)}
                      </option>
                    ))}
                  </FormikInput>
                </div>

                <div className="SignUpFormModal__form__field">
                  <h4 className="SignUpFormModal__form__field__label">
                    {t('sign_up_form.form.referral_code_label')}
                  </h4>

                  <FormikInput
                    hideError
                    name="referral_code"
                    minLength={3}
                    maxLength={25}
                    placeholder={t(
                      'sign_up_form.form.referral_code_placeholder'
                    )}
                  />
                </div>

                <Button
                  fluid
                  size="large"
                  loading={isSubmitting}
                  disabled={!isValid}
                >
                  {t('sign_up_form.submit')}
                </Button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  )
}

export default SignUpFormModal
